import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Pagination,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate, useSearchParams } from "react-router-dom";
import DeliveryPincodesDialog from "./DeliveryPincodesDialog";
import AddEditBuilding from "../../components/settings/AddEditBuilding";
import { getAllBuildingsWithPagination } from "../../services/WarehouseSettings";

const WarehouseSettings = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPincodesDialog, setShowPincodesDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const navigate = useNavigate();

  const loadBuildings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllBuildingsWithPagination(page - 1, pageSize);

      if (response && typeof response === "object") {
        const buildings = response.content || response.data || response || [];
        setRows(Array.isArray(buildings) ? buildings : []);
        setTotalPages(
          response.totalPages ||
            Math.ceil((response.totalElements || 0) / pageSize),
        );
        setTotalElements(response.totalElements || 0);
        // Update page size if it comes from API response
        if (response.size && response.size !== pageSize) {
          setPageSize(response.size);
        }
      } else {
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (err) {
      console.error("Load buildings error:", err);
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  useEffect(() => {
    if (searchParams.get("action") === "pincodes") {
      setShowPincodesDialog(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleAdd = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setOpen(true);
  };

  const paginatedRows = rows;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p: 1,
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={() => navigate("/dashboard")}
          sx={{ border: "none", textTransform: "none", color: "text.primary" }}
          startIcon={<ArrowBackIosNewOutlinedIcon fontSize="small" />}
        >
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowPincodesDialog(true)}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "transparent",
              borderColor: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
          }}
          endIcon={<SettingsOutlinedIcon sx={{ color: "primary.main" }} />}
        >
          Delivery Pin codes
        </Button>
      </Box>

      <Paper
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "grey.100",
          }}
        >
          <Typography variant="h6">All Buildings - {totalElements}</Typography>
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ borderRadius: "0.5rem", textTransform: "none", px: 2 }}
          >
            Add Building
          </Button>
        </Box>

        <Box
          sx={{
            p: 1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              maxHeight: "500px",
              overflow: "auto",
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E1",
                borderRadius: "4px",
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: "600px" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Building no.
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Building name
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Floors
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Rooms/Zone
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Racks
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Bins
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Capacity
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "background.default",
                      fontSize: "0.875rem",
                      p: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                      <Typography color="grey.500">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:hover": { bgcolor: "grey.50" } }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.id}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.numberOfFloors || 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.numberOfRooms || 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.numberOfRacks || 0}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", p: 1 }}
                      >
                        {row.numberOfBins || 0}
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={ row.availablePercentage === 0.0 && row.usedPercentage >= 100 ? row.usedPercentage : row.availablePercentage}
                            sx={{
                              height: "0.6rem",
                              width: "6rem",
                              borderRadius: "0.4rem",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: row.availablePercentage === 0.0 && row.usedPercentage >= 100 ? "error.main" : row.availablePercentage <= 30 ? "warning.main" : "success.main",
                              },
                              backgroundColor: "grey.light",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", textAlign: "center" }}
                          >
                            { row.availablePercentage === 0.0 && row.usedPercentage >= 100 ? row.usedPercentage : row.availablePercentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: "0.7rem",
                            py: "0.25rem",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: "16px",
                            textAlign: "center",
                            backgroundColor:
                              row.status === "Active"
                                ? "rgba(52, 199, 89, 0.10)"
                                : "rgba(255, 59, 48, 0.10)",
                            color:
                              row.status === "Active"
                                ? "success.main"
                                : "error.main",
                            border: "none",
                            minWidth: "120px",
                          }}
                        >
                          {row.status}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Button
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={() =>
                              navigate(
                                `/settings/warehouse/buildings/${row.id}/floors`,
                              )
                            }
                            sx={{
                              color: "grey.500",
                              textTransform: "none",
                              fontSize: "0.8rem",
                            }}
                          >
                            View
                          </Button>
                          <Button
                            startIcon={
                              <Box
                                component="img"
                                src="/edit.svg"
                                alt="Edit"
                                sx={{ width: "1.2rem", height: "1.2rem" }}
                              />
                            }
                            onClick={() => handleEdit(row)}
                            sx={{
                              color: "primary.main",
                              textTransform: "none",
                              fontSize: "0.8rem",
                            }}
                          >
                            Edit
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Paper>

      {totalElements > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <AddEditBuilding
        open={open}
        onClose={() => {
          setOpen(false);
          setEditData(null);
        }}
        editData={editData}
        onSuccess={(message: string) => {
          loadBuildings();
          setSnackbarMessage(message);
          setShowSnackbar(true);
        }}
      />

      <DeliveryPincodesDialog
        open={showPincodesDialog}
        onClose={() => setShowPincodesDialog(false)}
        onSave={() => {}}
      />

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity="success"
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: "success.main !important",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default WarehouseSettings;
